import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";

/**
 * The small pieces every Distribution editor is built from (`BE-15`).
 *
 * One copy, because the channel editor and the VOD editor are two views of the
 * SAME catalogue row - `CmsAsset`, `type: "channel"` or `type: "movie"` - and
 * two sets of look-alike form primitives is how one half starts accepting what
 * the other half refuses. The routes learned this the hard way and put their
 * shared rules in `contentAssetBody.ts`; this is the same argument one layer up.
 */

/**
 * A field whose label stays above the box.
 *
 * The design labels every input with static 12px bold text, so a floating label
 * that drops INTO an empty field would put half the form's labels in a
 * different place from the other half (selects always shrink).
 */
export function Field(props: TextFieldProps) {
  return (
    <TextField size="small" {...props} InputLabelProps={{ shrink: true }} />
  );
}

export function SectionHead({
  title,
  subtitle,
  aside,
}: {
  title: string;
  subtitle: string;
  aside?: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 2 }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
        <Typography
          sx={{
            typography: "title",
            fontWeight: "fontWeightSemibold",
            color: tokens.ink,
            mb: 0.75,
          }}
        >
          {title}
        </Typography>
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {subtitle}
        </Typography>
      </Box>
      {aside && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: "0 0 auto" }}
        >
          {aside}
        </Stack>
      )}
    </Stack>
  );
}

/** The design's soft pill - a count, a keyword, a genre. */
export function SoftChip({
  label,
  onDelete,
  title,
}: {
  label: string;
  onDelete?: () => void;
  title?: string;
}) {
  return (
    <Chip
      size="small"
      label={label}
      title={title}
      onDelete={onDelete}
      sx={{
        borderRadius: 999,
        bgcolor: tokens.brandTintSoft,
        color: tokens.ink,
        typography: "micro",
        fontWeight: "fontWeightMedium",
      }}
    />
  );
}

export type Status = "done" | "partial" | "missing";

export const STATUS_COLOR: Record<Status, string> = {
  done: tokens.success,
  partial: tokens.warning,
  missing: tokens.inkFaint,
};

export function StatusDot({ status }: { status: Status }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: STATUS_COLOR[status],
        flex: "0 0 auto",
      }}
    />
  );
}

/**
 * An editable list of short strings - keywords, audio languages, subtitles.
 *
 * Free text with an explicit Add rather than a picker, because every list this
 * draws is genuinely open upstream: `AssetKeyword` is `@@id([assetId, keyword])`
 * on a `VarChar(64)` column, so two spellings of one word are two rows. What it
 * will NOT do is split on commas - "Swedish, English" typed into one box is not
 * two languages, it is a parsing problem, and guessing which is a fine way to
 * store a language called `" english"`.
 */
export function StringListField({
  label,
  hint,
  value,
  placeholder,
  onChange,
  testId,
}: {
  label: string;
  hint?: string;
  value: string[];
  placeholder?: string;
  onChange: (next: string[]) => void;
  testId?: string;
}) {
  const { t } = useTranslation(["content", "common"]);
  const [entry, setEntry] = useState("");

  function add() {
    const next = entry.trim();
    if (!next || value.includes(next)) {
      setEntry("");
      return;
    }
    onChange([...value, next]);
    setEntry("");
  }

  return (
    <Box>
      <Typography
        variant="meta"
        sx={{ fontWeight: "fontWeightBold", color: tokens.ink, mb: 0.5 }}
      >
        {label}
      </Typography>
      {hint && (
        <Typography variant="micro" sx={{ color: tokens.inkMuted, mb: 1 }}>
          {hint}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1 }}
      >
        {value.map((item) => (
          <SoftChip
            key={item}
            label={item}
            onDelete={() => onChange(value.filter((v) => v !== item))}
          />
        ))}
        {value.length === 0 && (
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {t("editorBits.none")}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          value={entry}
          placeholder={placeholder}
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          inputProps={testId ? { "data-testid": testId } : undefined}
          sx={{ maxWidth: 260 }}
        />
        <Button size="small" variant="outlined" onClick={add}>
          {t("common:buttons.add")}
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * A card around one row of a repeated collection, with its own remove control.
 *
 * Every collection in this catalogue is destructively replaced on PATCH, so
 * "remove" here means "this row will not be restated", which means it will be
 * deleted. The card says so once, in the dialog, rather than in seven places.
 */
export function CollectionRow({
  title,
  onRemove,
  removeLabel,
  children,
}: {
  title: ReactNode;
  onRemove: () => void;
  removeLabel: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${tokens.divider}`,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="bodySm"
          sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
        >
          {title}
        </Typography>
        <Button
          size="small"
          onClick={onRemove}
          sx={{ color: tokens.error, minHeight: 0 }}
        >
          {removeLabel}
        </Button>
      </Stack>
      {children}
    </Box>
  );
}
